import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Event types for analytics tracking
export type AnalyticsEventType = 'pageview' | 'conversion';

// Event data structure sent to the backend
export interface AnalyticsEvent {
  type: AnalyticsEventType;
  page?: string;
  event?: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_medium?: string;
}

// Storage key for opt-out preference (client-side only)
const OPT_OUT_KEY = 'marvin_analytics_optout';

// Analytics endpoint (same-origin, proxied by nginx to FastAPI)
const ANALYTICS_ENDPOINT = '/analytics/collect';

/**
 * Check if analytics is opted out
 * Uses sessionStorage for privacy (cleared on browser close)
 */
const isOptedOut = (): boolean => {
  try {
    // Check sessionStorage for opt-out
    if (sessionStorage.getItem(OPT_OUT_KEY) === 'true') {
      return true;
    }
    // Check URL parameter for opt-out
    const params = new URLSearchParams(window.location.search);
    if (params.get('no_analytics') === '1') {
      // Store opt-out in sessionStorage for the session
      sessionStorage.setItem(OPT_OUT_KEY, 'true');
      return true;
    }
    return false;
  } catch {
    // If sessionStorage is not available, continue without opt-out
    return false;
  }
};

/**
 * Extract UTM parameters from URL
 */
const getUtmParams = (): Pick<AnalyticsEvent, 'utm_source' | 'utm_campaign' | 'utm_medium'> => {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
    };
  } catch {
    return {};
  }
};

/**
 * Send analytics event using navigator.sendBeacon
 * Fire-and-forget, non-blocking, works even on page unload
 */
const sendEvent = (event: AnalyticsEvent): void => {
  // Skip if opted out
  if (isOptedOut()) {
    return;
  }

  // Skip if sendBeacon is not supported (old browsers)
  if (!navigator.sendBeacon) {
    return;
  }

  try {
    // Enrich event with UTM parameters
    const enrichedEvent: AnalyticsEvent = {
      ...event,
      ...getUtmParams(),
    };

    // Create blob with JSON data
    const blob = new Blob(
      [JSON.stringify(enrichedEvent)],
      { type: 'application/json' }
    );

    // Fire-and-forget beacon
    navigator.sendBeacon(ANALYTICS_ENDPOINT, blob);
  } catch {
    // Silently fail - analytics should never break the app
  }
};

/**
 * Hook for automatic pageview tracking on route changes
 * 
 * Usage:
 * ```tsx
 * function App() {
 *   usePageviewTracking();
 *   return <Routes>...</Routes>;
 * }
 * ```
 */
export const usePageviewTracking = (): void => {
  const location = useLocation();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    // Avoid duplicate tracking on initial mount with same path
    if (previousPath.current === location.pathname) {
      return;
    }

    previousPath.current = location.pathname;

    // Send pageview event
    sendEvent({
      type: 'pageview',
      page: location.pathname,
    });
  }, [location.pathname]);
};

/**
 * Hook for manual analytics event tracking
 * 
 * Usage:
 * ```tsx
 * function ContactForm() {
 *   const trackEvent = useAnalyticsEvent();
 *   
 *   const handleSubmit = () => {
 *     // ... form logic
 *     trackEvent('conversion', { event: 'contact_form_submit', page: '/contact' });
 *   };
 * }
 * ```
 */
export const useAnalyticsEvent = () => {
  const trackEvent = useCallback(
    (type: AnalyticsEventType, extra?: Omit<AnalyticsEvent, 'type'>) => {
      sendEvent({
        type,
        ...extra,
      });
    },
    []
  );

  return trackEvent;
};

/**
 * Legacy hook for backward compatibility
 * Tracks a single event on component mount
 * 
 * Usage:
 * ```tsx
 * function LandingPage() {
 *   useAnalytics('pageview', { page: '/landing' });
 *   return <div>...</div>;
 * }
 * ```
 */
export const useAnalytics = (
  eventType: AnalyticsEventType,
  extra?: Omit<AnalyticsEvent, 'type'>
): void => {
  const hasTracked = useRef(false);

  useEffect(() => {
    // Only track once per component instance
    if (hasTracked.current) {
      return;
    }

    hasTracked.current = true;

    sendEvent({
      type: eventType,
      ...extra,
    });
  }, [eventType, extra]);
};

/**
 * Utility function for one-off event tracking (non-hook)
 * Use this for tracking events outside of React components
 * 
 * Usage:
 * ```ts
 * trackAnalyticsEvent('conversion', { event: 'pdf_download', page: '/resources' });
 * ```
 */
export const trackAnalyticsEvent = (
  type: AnalyticsEventType,
  extra?: Omit<AnalyticsEvent, 'type'>
): void => {
  sendEvent({
    type,
    ...extra,
  });
};

/**
 * Enable analytics opt-out for the current session
 */
export const optOutAnalytics = (): void => {
  try {
    sessionStorage.setItem(OPT_OUT_KEY, 'true');
  } catch {
    // Silently fail if sessionStorage is not available
  }
};

/**
 * Clear analytics opt-out for the current session
 */
export const optInAnalytics = (): void => {
  try {
    sessionStorage.removeItem(OPT_OUT_KEY);
  } catch {
    // Silently fail if sessionStorage is not available
  }
};

export default useAnalytics;
